import { transformStaticFilterToPropertySearchFilter } from '@dreamstack/accentro-contentful/src/utils/transformStaticFilterToPropertySearchFilter'
import { SSG_STATIC_PAGES } from '@dreamstack/accentro-contentful/src/config'
import { applyPropertiesFilter } from '@dreamstack/feature-components/src/hooks/useApplyPropertiesFilter'
import { propertyPriceVisibility } from '@dreamstack/feature-components/src/hooks/usePropertyPriceVisibility'
import { MainLayout } from '@dreamstack/feature-components/src/layout/MainLayout'
import type { PropertySearchFilterValues } from '@dreamstack/feature-components/src/next/PropertySearchFilter'
import { PropertyFilterOverview } from '@dreamstack/feature-components/src/property/filter/PropertyFilterOverview'
import { getPropertyOverviewRedirectUrl } from '@dreamstack/feature-components/src/property/routing/getPropertyOverviewRedirectUrl'
import { getCityUrl, UNKNOWN_CITY_URL_PART } from '@dreamstack/feature-components/src/property/routing/getPropertyUrl'
import { usePropertyOverviewUrl } from '@dreamstack/feature-components/src/property/routing/usePropertyOverviewUrl'
import type { IOtherLocaleUrlsContext, LocaleString } from '@dreamstack/i18n'
import {
  allLocales,
  OtherLocaleUrlsContextProvider,
  useTranslation,
} from '@dreamstack/i18n'
import { DreamstackSeo } from '@dreamstack/seo'
import filter from 'lodash/filter'
import find from 'lodash/find'
import includes from 'lodash/includes'
import map from 'lodash/map'
import uniqBy from 'lodash/uniqBy'
import type { FunctionComponent } from 'react'
import {
  fetchAllStaticFiltersWithSlugs,
  StaticFilterWithSlugAndSeoText,
} from '../../../lib/fetchAllStaticFiltersWithSlugs'
import { getStaticPathsPlus } from '../../../lib/getStaticPathsPlus'
import { getStaticPropsPlus } from '../../../lib/getStaticPropsPlus'
import { getAllProperties } from '../../../lib/prisma/properties'

const CityOverview: FunctionComponent<
  React.PropsWithChildren<{
    filter?: StaticFilterWithSlugAndSeoText
    noIndex?: boolean
  }>
> = ({ filter, noIndex }) => {
  const propertyOverviewUrl = usePropertyOverviewUrl()
  const otherLocaleUrls: IOtherLocaleUrlsContext = {
    de: { url: getCityUrl({ locale: 'de', city: filter?.city }) },
    en: { url: getCityUrl({ locale: 'en', city: filter?.city }) },
  }
  const t = useTranslation()
  return (
    <>
      <OtherLocaleUrlsContextProvider context={otherLocaleUrls}>
        <DreamstackSeo
          title={filter?.seo?.title ?? t('accentro:propertyOverview.title')}
          description={filter?.seo?.description ?? ''}
          nofollow={false}
          noindex={noIndex}
        />
        <MainLayout navbarProps={{ forceDarkTextColor: true }}>
          <PropertyFilterOverview
            staticFilter={filter}
            seoText={filter?.seoText ?? undefined}
            activatePropertySearchQueryUrlSync={true}
            forcePropertySearchQueryUrlSyncUrl={propertyOverviewUrl}
          />
        </MainLayout>
      </OtherLocaleUrlsContextProvider>
    </>
  )
}

export default CityOverview

export const getStaticProps = getStaticPropsPlus({
  getProps: async (ctx) => {
    const validProperties = await getAllProperties({
      locale: (ctx.locale as LocaleString) ?? ('de' as LocaleString),
    })

    const { params, locale } = ctx
    const slugPathParam = (params as Params).slug

    // Redirect if UNKNOWN_CITY_URL_PART
    if (locale && includes(UNKNOWN_CITY_URL_PART, slugPathParam)) {
      const destination = getPropertyOverviewRedirectUrl({
        locale: locale as LocaleString,
      })
      return {
        redirect: {
          destination,
        },
      }
    }

    const allStaticFilters = await fetchAllStaticFiltersWithSlugs({
      locale: locale as LocaleString,
    })

    const matchingFilter = find(
      allStaticFilters,
      ({ slug }) => slug === slugPathParam
    )
    if (!matchingFilter) {
      return { notFound: true }
    }

    const mappedFilter: PropertySearchFilterValues =
      transformStaticFilterToPropertySearchFilter({
        staticFilter: matchingFilter,
      })
    const filteredProperties = applyPropertiesFilter({
      filter: mappedFilter,
      validProperties,
      isReservationUi: false,
      propertyPriceVisibility,
    })
    const noIndex = filteredProperties.length < 3

    return { props: { filter: matchingFilter, noIndex } }
  },
})

export const getStaticPaths = getStaticPathsPlus({
  getPaths: async () => {
    if (!SSG_STATIC_PAGES) {
      return []
    }
    const staticFiltersByLocale = await Promise.all(
      allLocales.map(async (locale) => {
        return await fetchAllStaticFiltersWithSlugs({
          locale,
        })
      })
    )
    let allStaticFilters = staticFiltersByLocale.flat()
    // Empty urlPart breaks everything because it will overlap wth /properties
    allStaticFilters = filter(allStaticFilters, ({ slug }) => !!slug)
    allStaticFilters = uniqBy(allStaticFilters, ({ slug }) => slug)

    return map<StaticFilterWithSlugAndSeoText, { params: Params }>(
      allStaticFilters,
      (filter) => ({
        params: { slug: filter.slug ?? '' }, // Slug will alway be defined here, since we filtered out all filters without slug above.
      })
    )
  },
})

type Params = {
  slug: string
}
